import { Dealer } from "@/types/dealer";
import { CACHE_EXPIRATION } from "./services/cacheConfig";
import { isSSR } from "@/lib/isSSR";
import { debugLog, debugError } from '@/utils/debugLogger';
import { unifiedDealerCache } from '@/lib/cache/UnifiedDealerCache';
import { safeLocalStorage } from '@/utils/safeStorage';

// Define a global interface to hold the cache
declare global {
  interface Window {
    __DEALER_CACHE__: {
      [key: string]: {
        timestamp: number;
        data: Dealer[];
      };
    } | undefined;
    __PREFETCHED_DEALERS__?: {
      metal: string;
      timestamp: number;
      data: Dealer[];
    };
    // Add the cache version property to the Window interface
    __CACHE_VERSION?: string;
  }
}

// Initialize the cache in the global scope if it doesn't exist
if (typeof window !== 'undefined') {
  window.__DEALER_CACHE__ = window.__DEALER_CACHE__ || {};
}

/**
 * Get recommended dealers data from cache
 * @param key Cache key
 * @returns Cached dealers data if available and not expired, otherwise null
 */
export const getCachedRecommendedDealers = (key: string): Dealer[] | null => {
  if (typeof window === 'undefined' || !window.__DEALER_CACHE__) {
    return null;
  }
  
  const cachedData = window.__DEALER_CACHE__[key];
  
  if (cachedData && Date.now() - cachedData.timestamp < CACHE_EXPIRATION) {
    debugLog('cache', "Returning cached data", { key }, 'Cache');
    return cachedData.data;
  }
  
  return null;
};

/**
 * Cache recommended dealers data
 * @param key Cache key
 * @param data Dealers data to cache
 */
export const cacheRecommendedDealers = (key: string, data: Dealer[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  
  window.__DEALER_CACHE__ = window.__DEALER_CACHE__ || {};
  window.__DEALER_CACHE__[key] = {
    timestamp: Date.now(),
    data: data,
  };
  
  debugLog('cache', "Cached data", { key }, 'Cache');
};

/**
 * Store prefetched dealers for immediate use
 * @param metal Metal type (e.g., "ORO", "ARGENTO")
 * @param dealers Dealer data to prefetch
 */
export const setPrefetchedDealers = (metal: string, dealers: Dealer[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  
  window.__PREFETCHED_DEALERS__ = {
    metal,
    timestamp: Date.now(),
    data: dealers
  };
  
  debugLog('cache', "Prefetched dealers", { count: dealers.length, metal }, 'Cache');
};

/**
 * Get prefetched dealers from cache
 * @returns Prefetched dealers or null if none exist or expired
 */
export const getPrefetchedDealersFromCache = (): Dealer[] | null => {
  if (typeof window === 'undefined' || !window.__PREFETCHED_DEALERS__) {
    return null;
  }
  
  const prefetchedData = window.__PREFETCHED_DEALERS__;
  const PREFETCH_TTL = 300000; // 5 minutes
  
  if (Date.now() - prefetchedData.timestamp > PREFETCH_TTL) {
    // Clear expired prefetch data
    window.__PREFETCHED_DEALERS__ = undefined;
    return null;
  }
  
  return prefetchedData.data;
};

/**
 * Check if the cache version is outdated and clear the cache if necessary
 */
export const checkCacheVersion = () => {
  // Default cache version
  const DEFAULT_CACHE_VERSION = '1';
  
  // Get cache version safely without using import.meta.env
  // This works in both client and server environments
  const currentVersion = !isSSR() && 
                        typeof window !== 'undefined' && 
                        typeof window.document !== 'undefined' && 
                        // Try to get from a global variable if defined
                        (window.__CACHE_VERSION || DEFAULT_CACHE_VERSION);
                        
  const storedVersion = typeof localStorage !== 'undefined' ? safeLocalStorage.getItem('cacheVersion') : null;
  
  if (storedVersion !== currentVersion) {
    debugLog('cache', "Cache version outdated. Clearing cache.", undefined, 'Cache');
    
    // Clear all dealer-related caches
    invalidateAllDealerCaches();
    
    // Update the stored cache version
    if (typeof localStorage !== 'undefined') {
      safeLocalStorage.setItem('cacheVersion', currentVersion as string, true);
    }
  }
};

/**
 * Invalidate all dealer-related caches to force fresh data
 * Uses standardized cache keys from cacheConfig for consistency
 */
export const invalidateAllDealerCaches = () => {
  debugLog('cache', "Invalidating all dealer caches to force fresh data", undefined, 'Cache');
  
  // Clear localStorage cache for all metals
  try {
    const metals = ["ORO", "ARGENTO"];
    
    metals.forEach(metal => {
      // Standard key patterns (aligned with cacheConfig.ts generateCacheKey)
      const standardKey = `recommended-dealers-memory-${metal}`;
      const storageKey = `recommended-dealers-storage-${metal}`;
      
      // Clear localStorage cache - both legacy and new key patterns
      if (typeof localStorage !== 'undefined') {
        // New standardized keys
        safeLocalStorage.removeItem(standardKey);
        safeLocalStorage.removeItem(storageKey);
        safeLocalStorage.removeItem(`recommended-dealers-prefetch-${metal}`);
        
        // Legacy keys for backward compatibility
        safeLocalStorage.removeItem(`recommended-dealers-data-${metal}`);
        safeLocalStorage.removeItem(`recommended-dealers-timestamp-${metal}`);
        safeLocalStorage.removeItem(`fallback-dealers-${metal}`);
        safeLocalStorage.removeItem(`fallback-dealers-timestamp-${metal}`);
      }
      
      // Clear in-memory cache - both legacy and new key patterns, including coordinate-specific keys
      if (typeof window !== 'undefined' && window.__DEALER_CACHE__) {
        delete window.__DEALER_CACHE__[standardKey];
        delete window.__DEALER_CACHE__[storageKey];
        delete window.__DEALER_CACHE__[`recommended-dealers-data-${metal}`];
        
        // Clean ALL coordinate-specific cache keys for this metal
        Object.keys(window.__DEALER_CACHE__).forEach(key => {
          if (key.startsWith(`recommended-dealers-memory-${metal}`)) {
            delete window.__DEALER_CACHE__![key];
          }
        });
      }
    });

    // Clear prefetched dealers
    if (typeof window !== 'undefined') {
      window.__PREFETCHED_DEALERS__ = undefined;
    }
    
    // Invalida anche UnifiedDealerCache (usato da fetchDealersWithQuotations)
    try {
      unifiedDealerCache.clear();
    } catch (e) {
      debugError('cache', "Error clearing unified dealer cache", e, 'Cache');
    }
    
  } catch (error) {
    debugError('cache', "Error invalidating dealer caches", error, 'Cache');
  }
  
  debugLog('cache', "All dealer caches have been invalidated (including unified)", undefined, 'Cache');
};

// Add a global cache version property for client-side access
if (typeof window !== 'undefined') {
  window.__CACHE_VERSION = '2'; // Incremented to invalidate old cache without sortOrder
}
